// ============================================================================
// SAMPLE GL ACCOUNT ENDPOINTS (Reference Implementation)
// ============================================================================
// Endpoint definitions for General Ledger Accounts module

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

/**
 * Base path for GL account endpoints
 */
const BASE_PATH = '/gl-accounts';

/**
 * GL Account Endpoints
 */
export const sampleGLAccountEndpoints = {
  /**
   * Get all GL accounts
   * GET /gl-accounts
   */
  list: () => buildEndpoint(BASE_PATH),

  /**
   * Get GL account by ID
   * GET /gl-accounts/:id
   */
  getById: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Create new GL account
   * POST /gl-accounts
   */
  create: () => buildEndpoint(BASE_PATH),

  /**
   * Update GL account
   * PUT /gl-accounts/:id
   */
  update: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Delete GL account
   * DELETE /gl-accounts/:id
   */
  delete: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Search GL accounts
   * POST /gl-accounts/search
   */
  search: () => buildEndpoint(`${BASE_PATH}/search`),

  /**
   * Get GL accounts by category
   * GET /gl-accounts/by-category/:category
   */
  byCategory: (category: string) =>
    buildEndpointWithParams(`${BASE_PATH}/by-category/:category`, { category }),

  /**
   * Get chart of accounts
   * GET /gl-accounts/chart
   */
  chart: () => buildEndpoint(`${BASE_PATH}/chart`),

  /**
   * Get GL account hierarchy
   * GET /gl-accounts/hierarchy
   */
  hierarchy: () => buildEndpoint(`${BASE_PATH}/hierarchy`),

  /**
   * Get GL account balance
   * GET /gl-accounts/:id/balance
   */
  balance: (id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:id/balance`, { id }),

  /**
   * Get GL account children
   * GET /gl-accounts/:id/children
   */
  children: (id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:id/children`, { id }),

  /**
   * Get active GL accounts
   * GET /gl-accounts/active
   */
  active: () => buildEndpoint(`${BASE_PATH}/active`),

  /**
   * Get posting-enabled GL accounts
   * GET /gl-accounts/posting-enabled
   */
  postingEnabled: () => buildEndpoint(`${BASE_PATH}/posting-enabled`),

  /**
   * Bulk delete GL accounts
   * DELETE /gl-accounts/bulk
   */
  bulkDelete: () => buildEndpoint(`${BASE_PATH}/bulk`),

  /**
   * Export GL accounts
   * GET /gl-accounts/export
   */
  export: () => buildEndpoint(`${BASE_PATH}/export`),

  /**
   * Import GL accounts
   * POST /gl-accounts/import
   */
  import: () => buildEndpoint(`${BASE_PATH}/import`),
} as const;

/**
 * Export endpoints
 */
export default sampleGLAccountEndpoints;
